import { useEffect, useRef } from 'react';

import type { ClinicalNote } from '@/react-query/videa-notes/useExistingNotes';

import {
  evaluateConditionals,
  extractVariableDefinitions,
  getCurrentFields,
  getCurrentTemplate,
} from '@repo/videa-notes';

import {
  BLURB_DELIMITER,
  formatConsentBlurb,
  getNoteSummaryWithoutBlurb,
} from '../helpers';
import type { EditedFields } from '../types';

interface SyncNoteDataParams {
  selectedNote: ClinicalNote | undefined;
  noteWithTemplateData: ClinicalNote | undefined;
  noteContentRef: React.MutableRefObject<string>;
  prevContentRef: React.MutableRefObject<string>;
  prevTemplateRef: React.MutableRefObject<string | null>;
  hasWritebackEnabled: boolean;
  enableVoiceNotesConsent: boolean;
  isResettingRef: React.MutableRefObject<boolean>;
  setNoteContent: (content: string) => void;
  setPatientConsentBlurb: (blurb: string | undefined) => void;
  setEditedFields: (
    fields: EditedFields | ((prev: EditedFields) => EditedFields),
  ) => void;
}

/**
 * Synchronizes local UI state with backend note data.
 * Pure function that can be called on-demand without React lifecycle concerns.
 */
const syncNoteData = ({
  selectedNote,
  noteWithTemplateData,
  noteContentRef,
  prevContentRef,
  prevTemplateRef,
  hasWritebackEnabled,
  enableVoiceNotesConsent,
  isResettingRef,
  setNoteContent,
  setPatientConsentBlurb,
  setEditedFields,
}: SyncNoteDataParams): void => {
  // Skip sync during reset operations to avoid conflicts
  if (isResettingRef.current) return;

  // Case 1: No note selected - clear all state
  if (!selectedNote) {
    setNoteContent('');
    setPatientConsentBlurb(undefined);
    prevContentRef.current = '';
    prevTemplateRef.current = null;
    setEditedFields(null);
    return;
  }

  // Case 2: Note selected - extract and sync content
  const currentTemplate = getCurrentTemplate(noteWithTemplateData);

  // Extract the content to display:
  // - For template notes: use editedTemplate if available, otherwise use original template
  // - For regular notes: use editedSummary if available, otherwise use original summary
  const summaryContent =
    currentTemplate && currentTemplate.length > 0
      ? (noteWithTemplateData?.editedTemplate ?? currentTemplate)[0]
          ?.bodyTemplate || ''
      : noteWithTemplateData?.editedSummary ||
        noteWithTemplateData?.summary ||
        '';

  // For template notes, evaluate {{#if field}}...{{/if}} blocks before any ref or content
  // assignment so that the editor never displays raw Handlebars conditional syntax.
  // TODO: deprecate handling this on FE once FE moves away from using a template towards
  // using the (marked-up) summary directly from the BE.
  const isTemplateNote = Boolean(currentTemplate && currentTemplate.length > 0);
  const processedSummaryContent = isTemplateNote
    ? evaluateConditionals(
        summaryContent,
        Object.fromEntries(
          extractVariableDefinitions(getCurrentFields(noteWithTemplateData)).map(
            (def) => [def.varName, def.defaultValue],
          ),
        ),
      )
    : summaryContent;

  // Check if we should overwrite the current content.
  // We should skip overwriting if:
  // - Backend matches what we last saved (this is a refetch after our own save)
  // - AND user has typed new content since the save (current content doesn't match saved content)
  // In all other cases (switching notes, external changes, or no new user input), we should sync.
  const expectedRefContent = isTemplateNote
    ? prevTemplateRef.current
    : prevContentRef.current;
  const backendMatchesSavedContent = processedSummaryContent === expectedRefContent;
  const currentContentWithoutBlurb = getNoteSummaryWithoutBlurb(
    noteContentRef.current,
    hasWritebackEnabled,
  );
  const userHasUnsavedChanges =
    currentContentWithoutBlurb !== expectedRefContent;

  // Skip overwriting only when: backend matches our save AND user has typed new content
  const shouldSkipOverwrite =
    backendMatchesSavedContent && userHasUnsavedChanges;

  // Update tracking refs for change detection in auto-save
  // These refs track what's saved on the backend, regardless of whether we overwrite user input
  // prevTemplateRef tracks template content (null for non-template notes)
  prevTemplateRef.current =
    currentTemplate && currentTemplate.length > 0 ? processedSummaryContent : null;
  prevContentRef.current = processedSummaryContent;

  // Handle patient consent blurb based on writeback and consent settings
  const consent = selectedNote.patientConsent;
  if (consent && enableVoiceNotesConsent) {
    const blurb = formatConsentBlurb(consent);
    if (hasWritebackEnabled) {
      // With writeback: store blurb separately (will be written back to PMS)
      setPatientConsentBlurb(blurb);
      if (!shouldSkipOverwrite) {
        setNoteContent(processedSummaryContent);
      }
    } else {
      // Without writeback: append blurb to content with delimiter
      setPatientConsentBlurb(undefined);
      if (!shouldSkipOverwrite) {
        setNoteContent(`${processedSummaryContent}${BLURB_DELIMITER}${blurb}`);
      }
    }
  } else {
    // No consent or consent feature disabled: just set content without blurb
    setPatientConsentBlurb(undefined);
    if (!shouldSkipOverwrite) {
      setNoteContent(processedSummaryContent);
    }
  }

  // Clear edited fields when syncing to a new note (user hasn't edited this note yet)
  // Only clear if we overwrote content, otherwise preserve user's field edits
  if (!shouldSkipOverwrite) {
    setEditedFields(null);
  }
};

interface UseNoteSyncProps {
  selectedNote: ClinicalNote | undefined;
  noteWithTemplateData: ClinicalNote | undefined;
  /** Ref to current note content (updated on every change, read without causing re-renders) */
  noteContentRef: React.MutableRefObject<string>;
  /** Ref tracking last saved content for regular (non-template) notes */
  prevContentRef: React.MutableRefObject<string>;
  /** Ref tracking last saved template content (null for non-template notes) */
  prevTemplateRef: React.MutableRefObject<string | null>;
  hasWritebackEnabled: boolean;
  enableVoiceNotesConsent: boolean;
  isResettingRef: React.MutableRefObject<boolean>;
  setNoteContent: (content: string) => void;
  setPatientConsentBlurb: (blurb: string | undefined) => void;
  /** Setter for edited fields (cleared when syncing to a new note) */
  setEditedFields: (
    fields: EditedFields | ((prev: EditedFields) => EditedFields),
  ) => void;
}

/**
 * Synchronizes local UI state with backend note data when the selected note changes.
 *
 * This hook ensures that when a user selects a different note (or when note data is updated),
 * the editor's local state is updated to match the backend data. It handles:
 * - Extracting content from template-based or regular notes
 * - Managing patient consent blurbs (appending or storing separately)
 * - Updating tracking refs for change detection
 * - Resetting edited fields when switching notes
 *
 * Uses a ref to track the previous note ID to only sync when the note actually changes,
 * reducing unnecessary syncs and potential memory leaks.
 */
export const useNoteSync = ({
  selectedNote,
  noteWithTemplateData,
  hasWritebackEnabled,
  enableVoiceNotesConsent,
  isResettingRef,
  noteContentRef,
  prevContentRef,
  prevTemplateRef,
  setNoteContent,
  setPatientConsentBlurb,
  setEditedFields,
}: UseNoteSyncProps) => {
  // Track previous note ID and data reference to only sync when note actually changes
  const prevNoteIdRef = useRef<string | undefined>(undefined);
  const prevNoteDataRef = useRef<ClinicalNote | undefined>(undefined);

  useEffect(() => {
    const currentNoteId = selectedNote?.id;
    const prevNoteId = prevNoteIdRef.current;
    const prevNoteData = prevNoteDataRef.current;

    // Only sync if note ID changed or note data reference changed (for refetches)
    // This prevents unnecessary syncs and reduces memory leak risk
    const noteIdChanged = currentNoteId !== prevNoteId;
    const noteDataChanged = noteWithTemplateData !== prevNoteData;

    if (!noteIdChanged && !noteDataChanged) {
      return;
    }

    // Update refs before syncing to prevent re-syncing
    prevNoteIdRef.current = currentNoteId;
    prevNoteDataRef.current = noteWithTemplateData;

    syncNoteData({
      selectedNote,
      noteWithTemplateData,
      noteContentRef,
      prevContentRef,
      prevTemplateRef,
      hasWritebackEnabled,
      enableVoiceNotesConsent,
      isResettingRef,
      setNoteContent,
      setPatientConsentBlurb,
      setEditedFields,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNote?.id, noteWithTemplateData]);
};
